'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

const BRAND = '#0279e8';

export default function NavigationProgress() {
  const pathname = usePathname();
  const [width, setWidth] = useState(0); // 0 = idle (unmounted)
  const activeRef = useRef(false);
  const trickle = useRef<number | undefined>(undefined);
  const safety = useRef<number | undefined>(undefined);
  const hide = useRef<number | undefined>(undefined);
  const prevPath = useRef(pathname);

  const clearTimers = () => {
    if (trickle.current) window.clearInterval(trickle.current);
    if (safety.current) window.clearTimeout(safety.current);
    if (hide.current) window.clearTimeout(hide.current);
  };

  const finish = () => {
    if (!activeRef.current) return;
    activeRef.current = false;
    clearTimers();
    setWidth(100);
    hide.current = window.setTimeout(() => setWidth(0), 300);
  };

  const start = () => {
    if (activeRef.current) return;
    activeRef.current = true;
    clearTimers();
    setWidth(8);
    trickle.current = window.setInterval(() => {
      setWidth((w) => (w < 90 ? w + (90 - w) * 0.12 : w));
    }, 180);
    // safety net: don't hang forever if a navigation is cancelled
    safety.current = window.setTimeout(finish, 10000);
  };

  // START — capture-phase click on an internal link that changes the path.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey ||
        e.defaultPrevented
      )
        return;
      const anchor = (e.target as HTMLElement | null)?.closest?.('a');
      if (!anchor) return;
      const target = anchor.getAttribute('target');
      if ((target && target !== '_self') || anchor.hasAttribute('download'))
        return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return; // external
      if (url.pathname === window.location.pathname) return; // same page / hash / query-only
      start();
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // FINISH — when the path actually changes (ignored if no nav was started,
  // so browser back/forward doesn't flash the bar).
  useEffect(() => {
    if (pathname === prevPath.current) return;
    prevPath.current = pathname;
    finish();
  }, [pathname]);

  useEffect(() => () => clearTimers(), []);

  if (width === 0) return null;

  return (
    <div
      aria-hidden="true"
      data-nav-progress=""
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 2147483647,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${width}%`,
          background: BRAND,
          boxShadow: `0 0 10px ${BRAND}, 0 0 4px ${BRAND}`,
          transition: 'width 180ms ease',
        }}
      />
    </div>
  );
}
